import { levelStep, MeterAiMode, MeterType, nextLevelFrom, TOD_OFFSET_MINUTES } from "@next-platform/packages-ts-types";
import { formatClockMinuteLabel } from "../formatRulebookMeter";
const KEYWORDS_TEXT_MAX = 60;
const capKeywordsText = (words) => {
    const joined = words.join(", ");
    return joined.length > KEYWORDS_TEXT_MAX ? { text: `${joined.slice(0, KEYWORDS_TEXT_MAX - 1).trimEnd()}…`, fullText: joined } : { text: joined };
};
export var DefinitionLevelList;
(function (DefinitionLevelList) {
    DefinitionLevelList["VALUE"] = "value";
    DefinitionLevelList["TOD"] = "tod";
    DefinitionLevelList["DAY"] = "day";
})(DefinitionLevelList || (DefinitionLevelList = {}));
const formatDayRange = (from, to, translate) => {
    if (to === null) {
        return translate("rulebook:profile.dayRangeOpen", { from });
    }
    if (from === to) {
        return translate("rulebook:profile.daySingle", { day: from });
    }
    return translate("rulebook:profile.dayRange", { from, to });
};
const clockDriverRows = (meter, translate) => {
    const rows = [];
    if (meter.mode === "datetime") {
        rows.push({ direction: "flat", text: translate("rulebook:profile.driver.clockStep", { minutes: meter.step_minutes }) });
        if (meter.detect_skips) {
            rows.push({ direction: "flat", text: translate("rulebook:profile.driver.clockSkip") });
        }
        return rows;
    }
    if (meter.step_every_messages > 0) {
        rows.push({ direction: "flat", text: translate("rulebook:profile.driver.clockDayStep", { messages: meter.step_every_messages }) });
    }
    if (meter.detect_skips) {
        rows.push({ direction: "flat", text: translate("rulebook:profile.driver.clockSkip") });
    }
    return rows;
};
/**
 * "What moves this meter" rows for the definition Meters section: enabled AI driver rows
 * (skipped entirely in extract mode), keyword drivers, and the clock's built-in behaviour.
 */
export function meterDriverRows(meter, translate) {
    if (meter.type === MeterType.CLOCK) {
        return clockDriverRows(meter, translate);
    }
    const rows = [];
    // Dice meters carry no AI/keyword drivers on the contract.
    const ai = "ai" in meter ? meter.ai : undefined;
    if (ai?.enabled && ai.mode !== MeterAiMode.EXTRACT) {
        for (const row of ai.rows) {
            rows.push({ direction: row.impact >= 0 ? "up" : "down", text: row.condition });
        }
    }
    const keywords = "keywords" in meter ? meter.keywords : [];
    for (const keyword of keywords) {
        rows.push({ direction: keyword.delta >= 0 ? "up" : "down", ...capKeywordsText(keyword.words) });
    }
    return rows;
}
/**
 * Type subtitle for a definition meter card: `Bar · 0–100`, `Pips · {max}`, `Number · {unit}`,
 * `Dice · d{sides}`, `Story clock · day & time`.
 */
export function meterTypeSummary(meter, translate) {
    if (meter.type === MeterType.BAR) {
        const range = typeof meter.min === "number" && typeof meter.max === "number" ? ` · ${meter.min}–${meter.max}` : "";
        return `${translate("rulebook:profile.meterType.bar")}${range}`;
    }
    if (meter.type === MeterType.PIPS) {
        const max = typeof meter.max === "number" ? ` · ${meter.max}` : "";
        return `${translate("rulebook:profile.meterType.pips")}${max}`;
    }
    if (meter.type === MeterType.NUMBER) {
        const unit = meter.unit ? ` · ${meter.unit}` : "";
        let display = "";
        if (meter.display === "level") {
            display = translate("rulebook:profile.meterDisplay.level");
        }
        else if (meter.display === "number") {
            display = translate("rulebook:profile.meterDisplay.number");
        }
        const displayLabel = display ? `${unit ? ", " : " · "}${display}` : "";
        return `${translate("rulebook:profile.meterType.number")}${unit}${displayLabel}`;
    }
    if (meter.type === MeterType.DICE) {
        const sides = typeof meter.sides === "number" ? ` · d${meter.sides}` : "";
        return `${translate("rulebook:profile.meterType.dice")}${sides}`;
    }
    return `${translate("rulebook:profile.meterType.storyClock")} · ${translate("rulebook:profile.clockSubline")}`;
}
export function formatPublicLevelRange({ level, kind, translate, }) {
    if (kind === DefinitionLevelList.TOD) {
        const from = formatClockMinuteLabel({ minuteOfDay: level.from + TOD_OFFSET_MINUTES });
        return level.to === null ? `${from}+` : `${from}–${formatClockMinuteLabel({ minuteOfDay: level.to + TOD_OFFSET_MINUTES })}`;
    }
    if (kind === DefinitionLevelList.DAY) {
        return formatDayRange(level.from, level.to, translate);
    }
    return level.to === null ? `${level.from}+` : `${level.from}–${level.to}`;
}
export const publicLevelSegmentWeights = ({ meter, levels, kind, }) => {
    if (kind !== DefinitionLevelList.VALUE) {
        return levels.map(() => ({ weight: 1 }));
    }
    const step = levelStep(meter);
    const boundedWeights = levels.filter((level) => level.to !== null).map(({ from, to }) => Math.max(step, (to ?? from) - from + step));
    const openWeight = boundedWeights.length > 0 ? boundedWeights.reduce((total, weight) => total + weight, 0) / boundedWeights.length : 1;
    return levels.map(({ from, to }) => ({
        weight: to === null && meter.max === undefined ? openWeight : Math.max(step, (to ?? meter.max ?? from) - from + step),
    }));
};
/** Previous bound → `from` for a definition level row (engine derives `from` from the previous `to`). */
const levelFrom = (meter, kind, firstFrom, previousTo) => {
    if (previousTo === null || previousTo === undefined) {
        return firstFrom;
    }
    // Value levels honour counter decimals (1.25 → 1.26); clock bands step whole units.
    return kind === DefinitionLevelList.VALUE ? nextLevelFrom(meter, previousTo) : previousTo + 1;
};
/** Lower bound for the first value level of a meter (engine convention: min, 1 for dice, 0 for counters). */
const meterFirstFrom = (meter) => {
    if (meter.type === MeterType.BAR || meter.type === MeterType.PIPS) {
        return meter.min;
    }
    return meter.type === MeterType.DICE ? 1 : 0;
};
/**
 * Inclusive `from–to` text for a level row: value bounds (`0–24`, `75+` when open), clock
 * time-of-day bands (`HH:MM–HH:MM` — stored minutes since 06:00 converted back to wall time) and
 * clock day bands (`Day a–b`, `Day n+` when open).
 */
export function formatDefinitionLevelRange(meter, levels, index, kind, translate) {
    const level = levels[index];
    if (!level) {
        return "";
    }
    const previousTo = index > 0 ? levels[index - 1]?.to : null;
    if (kind === DefinitionLevelList.TOD) {
        const from = formatClockMinuteLabel({ minuteOfDay: levelFrom(meter, kind, 0, previousTo) + TOD_OFFSET_MINUTES });
        return level.to === null ? `${from}+` : `${from}–${formatClockMinuteLabel({ minuteOfDay: level.to + TOD_OFFSET_MINUTES })}`;
    }
    if (kind === DefinitionLevelList.DAY) {
        const from = levelFrom(meter, kind, 1, previousTo);
        return formatDayRange(from, level.to, translate);
    }
    const from = levelFrom(meter, kind, meterFirstFrom(meter), previousTo);
    return level.to === null ? `${from}+` : `${from}–${level.to}`;
}
